import { z } from 'zod';

import { AddOnStatus, RequestAddOnSchema, ResponseAddOnSchema } from './add-on';
import { buildQueryString } from '../../utils/querystring';
import { LanguageCode } from '../brand/brand';
import { makeEndpoint } from '../common';
import { HostCommissionType } from '../experience/hosts';

export enum ListingAddOnLimitType {
  PER_PARTICIPANT = 'PER_PARTICIPANT',
  PER_BOOKING = 'PER_BOOKING',
  PER_SESSION = 'PER_SESSION',
}

export enum ListingAddOnUnavailableReason {
  SOLD_OUT = 'SOLD_OUT',
  BOOKING_CUTOFF = 'BOOKING_CUTOFF',
}

const ListingAddOnLimitSchema = z.strictObject({
  type: z.nativeEnum(ListingAddOnLimitType),
  value: z.number().nonnegative().finite().int(),
});

const PropertiesOnlyForListingAddOnSchema = z.strictObject({
  limit: ListingAddOnLimitSchema.nullable(),
  onlineBookingCutoffHours: z.number().max(1000).nullable(),
  hostCommissionType: z.nativeEnum(HostCommissionType),
  hostCommission: z.number().nonnegative().finite(),
  experienceId: z.string().uuid().nullable(),
  resourceGroupId: z.string().uuid().nullable(),
  resourceGroupCollectionId: z.string().uuid().nullable(),
  addOnId: z.string().uuid(),
});

const RequestListingAddOnSchema = RequestAddOnSchema.merge(PropertiesOnlyForListingAddOnSchema).partial({
  experienceId: true,
  resourceGroupId: true,
  resourceGroupCollectionId: true,
  addOnId: true,
  onlineBookingCutoffHours: true,
});
export const ResponseListingAddOnSchema = ResponseAddOnSchema.merge(PropertiesOnlyForListingAddOnSchema);

export const PublicResponseListingAddOnSchema = ResponseListingAddOnSchema.pick({
  id: true,
  label: true,
  description: true,
  status: true,
  price: true,
  picture: true,
  taxes: true,
  limit: true,
  onlineBookingCutoffHours: true,
});

const CreateListingAddOnRequestSchema = z.union([
  RequestListingAddOnSchema.extend({ addOnId: z.undefined().optional() }),
  RequestListingAddOnSchema.partial().required({ addOnId: true, hostCommissionType: true, hostCommission: true }),
]);

const UpdateListingAddOnRequestSchema = RequestListingAddOnSchema.partial().omit({
  experienceId: true,
  resourceGroupId: true,
  resourceGroupCollectionId: true,
  addOnId: true,
});

export const CreateListingAddOnEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/listing-add-ons`,
  responseSchema: z.void(),
  requestSchema: CreateListingAddOnRequestSchema,
});

const GetListingAddOnsQuerySchema = z
  .object({
    experienceId: z.string().uuid(),
    resourceGroupCollectionId: z.string().uuid(),
    resourceGroupId: z.string().uuid(),
    status: z.nativeEnum(AddOnStatus),
    language: z.nativeEnum(LanguageCode).optional().nullable(),
    bookingId: z.string().uuid().optional(),
  })
  .partial()
  .strict()
  .refine(
    (value: {
      experienceId?: string | undefined;
      resourceGroupCollectionId?: string | undefined;
      resourceGroupId?: string | undefined;
    }) => !!value.experienceId || !!value.resourceGroupCollectionId || !!value.resourceGroupId,
    'At least one of experienceId, resourceGroupCollectionId, or resourceGroupId must be provided.',
  );
const InternalResponseListingAddOnSchema = ResponseListingAddOnSchema.extend({
  sessionAvailability: z
    .object({
      available: z.boolean(),
      availableQuantity: z.number().int().nonnegative().nullable(),
      unavailableReason: z.nativeEnum(ListingAddOnUnavailableReason).optional(),
    })
    .strict()
    .nullable(),
});

export const GetListingAddOnsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetListingAddOnsQuerySchema> }) =>
    `v1/brands/${brandId}/listing-add-ons?${buildQueryString(query)}`,
  responseSchema: z.object({ items: z.array(InternalResponseListingAddOnSchema) }).strict(),
  requestSchema: z.void(),
  querySchema: GetListingAddOnsQuerySchema,
});

export const DeleteListingAddOnEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, listingAddOnId }: { brandId: string; listingAddOnId: string }) =>
    `v1/brands/${brandId}/listing-add-ons/${listingAddOnId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const UpdateListingAddOnEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, listingAddOnId }: { brandId: string; listingAddOnId: string }) =>
    `v1/brands/${brandId}/listing-add-ons/${listingAddOnId}`,
  responseSchema: z.void(),
  requestSchema: UpdateListingAddOnRequestSchema,
});

export const OrderListingAddOnRequestSchema = z
  .object({
    listingAddOns: z.array(z.object({ id: z.string().uuid() }).strict()).min(1),
  })
  .strict();

export const OrderListingAddOnEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/order-listing-add-ons`,
  requestSchema: OrderListingAddOnRequestSchema,
  responseSchema: z.void(),
});

export enum ListingAddOnField {
  LABEL = 'label',
  DESCRIPTION = 'description',
}
